import {
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
  Typography
} from '@material-ui/core'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import RefreshIcon from '@material-ui/icons/Refresh'
import { FC, useState } from 'react'

import { getTranslate } from '../../localization'
import { Permission } from '../../types/role'
import PermissionRow from '../PermissionRow'
import useStyles from './index.styles'

interface PermissionBoxProps {
  permission: Permission
  initialPermissions: Permission[]
  temporaryPermissions: Permission[]
  setTemporayPermissions: (p: Permission[]) => void
}
export const PermissionBox: FC<PermissionBoxProps> = ({
  permission,
  initialPermissions,
  temporaryPermissions,
  setTemporayPermissions
}) => {
  const classes = useStyles()
  const [isExpanded, setisExpanded] = useState<boolean>(false)

  const allChecked = temporaryPermissions
    .find((res) => res.permissionName === permission.permissionName)
    ?.subPermissions.every((sr) =>
      sr.actions
        .filter((act) => !act.disabled)
        .every((act) => act.selected && !act.disabled)
    )

  const noneChecked = temporaryPermissions
    .find((res) => res.permissionName === permission.permissionName)
    ?.subPermissions.every((sr) =>
      sr.actions.filter((act) => !act.disabled).every((act) => !act.selected)
    )

  const handleSelectAll = (checked: boolean) => {
    const updatedPermissions = temporaryPermissions.map((perm) => {
      if (perm.permissionName !== permission.permissionName) {
        return perm
      }

      return {
        ...perm,
        subPermissions: perm.subPermissions.map((subPermission) => ({
          ...subPermission,
          actions: subPermission.actions.map((action) => ({
            ...action,
            selected: !action.disabled ? checked : false
          }))
        }))
      }
    })

    setTemporayPermissions(updatedPermissions)
  }

  const handleResetAll = () => {
    const initPermission = initialPermissions.find(
      (p) => p.permissionName === permission.permissionName
    )

    if (!initPermission) return

    const updatedPermissions = temporaryPermissions.map((perm) =>
      perm.permissionName === permission.permissionName ? initPermission : perm
    )

    setTemporayPermissions(updatedPermissions)
  }

  return (
    <div className={classes.root}>
      <div className={classes.mainRow}>
        <div className={classes.flex}>
          <IconButton onClick={() => setisExpanded(!isExpanded)}>
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Typography variant="h4" className={classes.title}>
            {getTranslate(permission.permissionName)}
          </Typography>
        </div>
        {isExpanded && (
          <div className={classes.flex}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  size="small"
                  checked={allChecked}
                  indeterminate={!allChecked && !noneChecked}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              }
              label={getTranslate('Select All')}
            />
            <div className={[classes.flex, classes.gap].join(' ')}>
              <Typography variant="body1">{getTranslate('Reset')}</Typography>
              <IconButton
                onClick={() => {
                  handleResetAll()
                }}>
                <RefreshIcon fontSize="small" className={classes.resetIcon} />
              </IconButton>
            </div>
          </div>
        )}
      </div>
      <Collapse in={isExpanded}>
        <div className={classes.tableHead}>
          <div>
            <Typography variant="h4">{getTranslate('Name')}</Typography>
          </div>
          <div>
            <Typography variant="h4">{getTranslate('Create')}</Typography>
          </div>
          <div>
            <Typography variant="h4">{getTranslate('Read')}</Typography>
          </div>
          <div>
            <Typography variant="h4">{getTranslate('Edit')}</Typography>
          </div>
          <div>
            <Typography variant="h4">{getTranslate('Delete')}</Typography>
          </div>
        </div>
        {permission.subPermissions &&
          permission.subPermissions.map((subPermission, index) => {
            return (
              <PermissionRow
                key={index}
                permission={permission}
                subPermission={subPermission}
                temporaryPermissions={temporaryPermissions}
                setTemporayPermissions={setTemporayPermissions}
              />
            )
          })}
      </Collapse>
    </div>
  )
}

export default PermissionBox
